import { useEffect, useState } from "react";
import { Gift, Copy, Check, Users } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { TierBadge } from "@/components/TierBadge";

export function ReferralPanel() {
  const [d, setD] = useState(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => { api.get("/referrals").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="glass-card h-40 animate-pulse" />;
  const link = `${window.location.origin}${d.link_path}`;
  const copy = async () => {
    await navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success("Referral link copied");
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="glass-card p-6" data-testid="referral-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="font-display flex items-center gap-2 text-2xl font-bold uppercase tracking-tight text-white"><Gift size={20} className="text-emerald-400" /> Refer a friend, earn a free month</h2>
          <p className="mt-1 text-xs text-slate-500">The moment a friend signs up you get +3 bonus picks and an extra SGP a day for 3 days (emailed to you instantly). When they subscribe to Basic or Premium, your next month is on us — applied to your Stripe invoice, or saved as a credit.</p>
        </div>
        <div className="flex gap-6 text-right">
          <div><p className="label-caps">Invited</p><p className="font-mono-num text-2xl font-bold text-white" data-testid="referral-invited-count">{d.invited.length}</p></div>
          <div><p className="label-caps">Months earned</p><p className="font-mono-num text-2xl font-bold text-emerald-400" data-testid="referral-months-earned">{d.months_earned}</p></div>
          {d.bonus_active && <div><p className="label-caps">Bonus picks</p><p className="font-mono-num text-2xl font-bold text-orange-300" data-testid="referral-bonus-active" title={`+${d.bonus_active.picks} picks & +${d.bonus_active.sgps} SGP a day through ${d.bonus_active.until}`}>+{d.bonus_active.picks}<span className="text-xs text-slate-500">/day</span></p></div>}
          {d.credits > 0 && <div><p className="label-caps">Credits</p><p className="font-mono-num text-2xl font-bold text-amber-400" data-testid="referral-credits">{d.credits}</p></div>}
        </div>
      </div>
      <div className="mt-5 flex flex-col gap-2 sm:flex-row">
        <input data-testid="referral-link-input" readOnly value={link} className="input-dark font-mono-num flex-1 text-xs" onFocus={(e) => e.target.select()} />
        <button data-testid="referral-copy-btn" onClick={copy} className="btn-primary">{copied ? <Check size={15} /> : <Copy size={15} />} Copy link</button>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">Your code: <span className="font-mono-num font-bold text-slate-300" data-testid="referral-code">{d.code}</span></p>
      {d.invited.length > 0 && (
        <ul className="mt-5 divide-y divide-[#26334D]/60 rounded-lg border border-[#26334D] bg-[#0B0E14]/60" data-testid="referral-invited-list">
          {d.invited.map((f, i) => (
            <li key={i} className="flex items-center gap-3 px-3 py-2 text-sm">
              <Users size={14} className="text-slate-500" />
              <span className="flex-1 font-semibold text-white">{f.name}</span>
              <TierBadge tier={f.tier} />
              <span className={`text-xs font-bold uppercase ${f.rewarded ? "text-emerald-400" : "text-slate-500"}`}>{f.rewarded ? "free month earned" : "not subscribed yet"}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
